import { createReducer } from '@reduxjs/toolkit';
import {
  clearEmployeeAction,
  clearExistingEmployeeInfoAtOnboarding,
  getCompanyEmployeeInvitationLinksError,
  getCompanyEmployeeInvitationLinkSuccess,
  getEmployeeAction,
  getEmployeeErrorAction,
  saveEmployeeErrorAction,
  saveEmployeeSuccessAction,
  setActiveEmployeeDropdownItems,
  setCompanyEmployeeInvitationLinks,
  setEmployeeError,
  setEmployeeRequestsLoadingAction,
  setEmployeesAction,
  setEmployeesCountAction,
  setEmployeesDropdownItems,
  setEmployeesErrorAction,
  setEmployeesWithWorkerclass,
  setExistingEmployeeInfoAtOnboarding,
  setInvitationLinkInvalid,
  setOnboarding,
  setOnboardingEmployeeDetails,
  setOnboardingWithGeneralLink,
  updateEmployeeErrorAction,
  updateEmployeeSuccessAction,
} from 'store/actions/employee-actions';
import { IEmployeeGridDetails } from 'types/employee';
import IEmployeeState from 'types/employee/IEmployeeState';

const initialState: IEmployeeState = {
  saveEmployeeSuccess: false,
  saveEmployeeError: false,
  employees: [],
  setEmployeesError: false,
  employee: null,
  getEmployeeError: false,
  updateEmployeeSuccess: false,
  updateEmployeeError: false,
  employeesWithWorkerclass: [],
  employeesDropdownItems: [],
  onboardingDetails: null,
  loading: false,
  onboarding: false,
  onboardingWithGeneralLink: false,
  employeesCount: null,
  employeeError: null,
  numberOfEmployees: null,
  totalPages: 0,
  currentPage: 0,
  companyEmployeeInvitationLinks: [],
  companyEmployeeInvitationLinkError: null,
  companyEmployeeInvitationLinkSuccess: false,
  isInvitationTokenInvalid: false,
  activeEmployeeDropdownItems: [],
  existingEmployeeInfoAtOnboarding: null,
};

const generateEmployeesWithRightURL = (employees: IEmployeeGridDetails[]) => {
  const auxEmployees = employees.map((e: IEmployeeGridDetails) => {
    return {
      ...e,
      pictureUpload: `${e.pictureUpload}?time=${Date.now()}`,
    };
  });
  return auxEmployees;
};

const employeeReducer = createReducer(initialState, builder => {
  return builder
    .addCase(setEmployeeRequestsLoadingAction, (state, action) => ({ ...state, loading: action.payload }))
    .addCase(saveEmployeeSuccessAction, (state, action) => ({ ...state, saveEmployeeSuccess: action.payload }))
    .addCase(saveEmployeeErrorAction, (state, action) => ({ ...state, saveEmployeeError: action.payload }))
    .addCase(setEmployeesAction, (state, action) => {
      const updatedEmployees = generateEmployeesWithRightURL(action.payload.items);
      const isFirstPage = action.payload.pageIndex === 1;
      return {
        ...state,
        employees: isFirstPage ? updatedEmployees : [...state.employees, ...updatedEmployees],
        numberOfEmployees: action.payload.numberOfResults,
        totalPages: action.payload.totalPages,
        currentPage: action.payload.pageIndex,
      };
    })
    .addCase(setEmployeesErrorAction, (state, action) => ({ ...state, setEmployeesError: action.payload }))
    .addCase(getEmployeeAction, (state, action) => ({ ...state, employee: action.payload }))
    .addCase(getEmployeeErrorAction, (state, action) => ({ ...state, getEmployeeError: action.payload }))
    .addCase(updateEmployeeSuccessAction, (state, action) => ({ ...state, updateEmployeeSuccess: action.payload }))
    .addCase(updateEmployeeErrorAction, (state, action) => ({ ...state, updateEmployeeError: action.payload }))
    .addCase(setEmployeesWithWorkerclass, (state, action) => ({ ...state, employeesWithWorkerclass: action.payload }))
    .addCase(setEmployeesDropdownItems, (state, action) => ({ ...state, employeesDropdownItems: action.payload }))
    .addCase(clearEmployeeAction, state => ({
      ...state,
      saveEmployeeSuccess: false,
      saveEmployeeError: false,
      employee: null,
      getEmployeeError: false,
      updateEmployeeError: false,
      updateEmployeeSuccess: false,
      existingEmployeeInfoAtOnboarding: null,
      onboardingDetails: null,
      loading: false,
    }))
    .addCase(setOnboardingEmployeeDetails, (state, action) => ({ ...state, onboardingDetails: action.payload }))
    .addCase(setOnboarding, (state, action) => ({ ...state, onboarding: action.payload }))
    .addCase(setOnboardingWithGeneralLink, (state, action) => ({ ...state, onboardingWithGeneralLink: action.payload }))
    .addCase(setEmployeesCountAction, (state, action) => ({ ...state, employeesCount: action.payload }))
    .addCase(setEmployeeError, (state, action) => ({ ...state, employeeError: action.payload }))
    .addCase(getCompanyEmployeeInvitationLinkSuccess, (state, action) => ({
      ...state,
      companyEmployeeInvitationLinkSuccess: action.payload,
    }))
    .addCase(getCompanyEmployeeInvitationLinksError, (state, action) => ({
      ...state,
      companyEmployeeInvitationLinkError: action.payload,
    }))
    .addCase(setCompanyEmployeeInvitationLinks, (state, action) => ({
      ...state,
      companyEmployeeInvitationLinks: action.payload,
    }))
    .addCase(setInvitationLinkInvalid, (state, action) => ({
      ...state,
      isInvitationTokenInvalid: action.payload,
    }))
    .addCase(setActiveEmployeeDropdownItems, (state, action) => ({
      ...state,
      activeEmployeeDropdownItems: action.payload,
    }))
    .addCase(setExistingEmployeeInfoAtOnboarding, (state, action) => ({
      ...state,
      existingEmployeeInfoAtOnboarding: action.payload,
    }))
    .addCase(clearExistingEmployeeInfoAtOnboarding, state => ({
      ...state,
      existingEmployeeInfoAtOnboarding: null,
    }));
});

export default employeeReducer;
